import { css } from "@emotion/react";
import styled from "@emotion/styled";
import * as React from "react";
import { FC, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

import { Add, Info } from "@livechat/design-system-icons";
import {
  Alert,
  Button,
  DesignToken,
  Display,
  Heading,
  Icon,
  PromoBannerV2,
  RadiusToken,
  SpacingToken,
  Switch,
  Tag,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import { EmbeddedSectionHeaderTitle } from "components/EmbeddedSectionHeaderTitle";
import { ExternalLink } from "components/external-link";
import { IconsEnum, Icon as LegacyIcon } from "components/icon";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { StyledLink } from "components/links/StyledLink";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import {
  SettingsTableColumn as RawColumn,
  SettingsTableRow as Row,
  SettingsTable as Table,
} from "components/settings-table";
import { HelpCenter, TextHelpCenter } from "constants/help-center";
import { SettingsSection } from "constants/settings/sections";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { isInIframe } from "helpers/app";
import useSettingsSection from "hooks/settings/use-settings-section";
import { useIframeNavigateTo } from "hooks/use-iframe-navigate-to";
import { useToggle } from "hooks/use-toggle";
import { UpsellAlert } from "routes/subscription/UpsellAlert";
import { AgentActions, selectHasSeenEmailTemplatesBuilderPromoBanner, selectIsAdmin } from "store/entities/agents";
import { EmailTemplateActions } from "store/entities/email-template/actions";
import { EmailTemplateTypeEnum } from "store/entities/email-template/interfaces";
import { selectEmailTemplatesList } from "store/entities/email-template/selectors";
import {
  LicenseActions,
  selectDefaultTemplateId,
  selectHasSupportUkraineFooter,
  selectMaxTemplatesPerLicense,
} from "store/entities/license";
import { selectTeamsAlphabetically } from "store/entities/teams";
import { withVar } from "themes/helpers";

import SettingsViewContainer from "../ViewContainer";
import { TeamsPreview } from "../components/TeamsPreview";
import { SettingsRoutes } from "../constants";
import { ContentWrapper, PageWrapper } from "../styled";

import TemplateListContextMenu from "./TemplateListContextMenu";
import { EditorTypeModal } from "./components/EditorTypeModal";

const UkraineFooterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
  border: 1px solid ${withVar(DesignToken.BorderBasicSecondary)};
  margin-bottom: 25px;
  border-radius: var(${RadiusToken.Radius3});
  padding: 16px;
  justify-content: space-between;
  margin-top: 32px;
`;

const StyledPreviewHeader = styled.p`
  && {
    font-weight: 600;
    font-size: 12px;
    line-height: 20px;
    color: ${withVar(DesignToken.ContentBasicSecondary)};
    margin: 5px 0;
  }
`;

const StyledPreviewText = styled.p`
  font-family: Helvetica;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0px;
  text-align: left;
  margin: 0;
`;

const Column: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => <RawColumn paddingDesktop="1em 1.5em">{children}</RawColumn>;

export const TemplatesList: FC = () => {
  const isEmbeddedInIframe = isInIframe();

  const { navigateTo } = useIframeNavigateTo();
  const [deactivateFooterModal, toggleDeactivateFooterModal] = useToggle(false);
  const [deleteTemplateModal, toggleDeleteTemplate] = useToggle(false);
  const [editorTypeModal, toggleEditorTypeModal] = useToggle(false);
  const [currentTemplateId, setCurrentTemplateId] = useState("");
  const isAdmin = useSelector(selectIsAdmin);
  const templates = useSelector(selectEmailTemplatesList);
  const teams = useSelector(selectTeamsAlphabetically);
  const defaultTemplateId = useSelector(selectDefaultTemplateId);
  const hasSeenEmailTemplatesBuilderPromoBanner = useSelector(selectHasSeenEmailTemplatesBuilderPromoBanner);
  const maxTemplatesPerLicense = useSelector(selectMaxTemplatesPerLicense);
  const hasSupportUkraineFooter = useSelector(selectHasSupportUkraineFooter);
  const maxTemplatesLimitReached = !isEmbeddedInIframe && templates.length >= maxTemplatesPerLicense;
  const history = useHistory();
  const dispatch = useDispatch();
  const shouldShowVisualBuilderPromoBanner = !hasSeenEmailTemplatesBuilderPromoBanner && isAdmin;

  useSettingsSection(SettingsSection.EmailTemplates);

  useEffect(() => {
    trackEngagement("Settings: Email template section opened", EventPlace.Settings);
    dispatch(EmailTemplateActions.fetch());
  }, [dispatch]);

  const handlePromoBannerClose = (): void => {
    dispatch(
      AgentActions.setFlag({
        name: "hasSeenEmailTemplatesBuilderPromoBanner_26472914d0ad05ac88ccff5bbc3c9d23",
        value: true,
      })
    );
    trackEngagement("Email templates: Promo banner closed", EventPlace.Settings);
  };
  const toggleSupportUkraineFooter = useCallback(() => {
    if (hasSupportUkraineFooter) {
      dispatch(
        LicenseActions.setUkraineSupportFooter({ supportUkraineFooter: false, requestSource: "email-template" })
      );
    } else {
      dispatch(LicenseActions.setUkraineSupportFooter({ supportUkraineFooter: true, requestSource: "email-template" }));
    }
    toggleDeactivateFooterModal();
  }, [dispatch, hasSupportUkraineFooter, toggleDeactivateFooterModal]);

  const handleDeleteTemplate = (): void => {
    dispatch(EmailTemplateActions.delete({ id: currentTemplateId }));
    setCurrentTemplateId("");
    toggleDeleteTemplate();
  };

  const handleDeleteClick = (id: string): void => {
    setCurrentTemplateId(id);
    toggleDeleteTemplate();
  };

  const handleGoToHTMLEditor = (): void => {
    trackEngagement("Email templates: Create template button entry", EventPlace.Settings, {
      templateEditor: "HTML",
    });
    history.push(`${SettingsRoutes.EmailTemplates}/add`);
  };

  const handleGoToVisualBuilder = (): void => {
    history.push(`${SettingsRoutes.EmailTemplates}/add/visualBuilder`);
    trackEngagement("Email templates: Create template button entry", EventPlace.Settings, {
      templateEditor: "Visual",
    });
  };
  return (
    <SettingsViewContainer
      title="Email templates"
      customHeader={
        isEmbeddedInIframe ? (
          <IframeHeader>
            <EmbeddedSectionHeaderTitle
              title="Email templates"
              description={
                <Text>
                  Personalize the look of your emails with email templates. Make all your messages match your brand.{" "}
                  <Button
                    kind="link-inverted"
                    href={TextHelpCenter.EMAIL_TEMPLATE}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Learn more
                  </Button>
                </Text>
              }
            />
            <Button
              size="compact"
              kind="high-contrast"
              onClick={toggleEditorTypeModal}
              data-testid="create-template"
              disabled={!isAdmin}
              icon={<Icon source={Add} />}
            >
              New email template
            </Button>
          </IframeHeader>
        ) : undefined
      }
    >
      <PageWrapper>
        <ContentWrapper
          css={{
            // TODO: remove this once margin from p tags is removed from ContentWrapper component
            "& p": { margin: 0 },
            ...(isEmbeddedInIframe ? { padding: withVar(SpacingToken.Spacing8) } : {}),
          }}
        >
          {shouldShowVisualBuilderPromoBanner && (
            <Block marginBottom={withVar(SpacingToken.Spacing5)}>
              <PromoBannerV2
                onClose={handlePromoBannerClose}
                css={css({
                  maxWidth: isEmbeddedInIframe ? "100%" : "900px",
                  background: withVar(DesignToken.SurfaceGradient07),
                })}
              >
                <Flex
                  column
                  gap={withVar(SpacingToken.Spacing1)}
                  css={isEmbeddedInIframe ? css({ maxWidth: "720px" }) : undefined}
                >
                  <Display>Email template builder</Display>
                  <Text>
                    Now you can use a simple email builder to create email templates. Design your template appearance,
                    content, and footer using the intuitive visual editor.
                  </Text>
                </Flex>
              </PromoBannerV2>
            </Block>
          )}
          {isAdmin && hasSupportUkraineFooter && (
            <UkraineFooterWrapper>
              <Flex>
                <Flex column margin="0 10px 0 0">
                  <LegacyIcon name={IconsEnum.UkraineHeart} width="24px" height="24px" />
                </Flex>
                <Flex column>
                  <Text bold customColor={withVar(DesignToken.ContentBasicPrimary)}>
                    Want to help Ukraine ?
                  </Text>
                  <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>
                    Use a special footer in your email template linking to the donation page.
                  </Text>
                  <StyledPreviewHeader>FOOTER PREVIEW</StyledPreviewHeader>
                  <StyledPreviewText>
                    We support <strong>Ukraine</strong> 🇺🇦 <br />
                    If you want to help,{" "}
                    <ExternalLink
                      href="https://www.livechat.com/support-ukraine/?utm_source=hd_ticket_footer&utm_medium=referral&utm_campaign=support-ukraine"
                      onClick={() => trackEngagement("Settings: Donate Ukraine link clicked", EventPlace.Settings)}
                    >
                      donate here
                    </ExternalLink>
                  </StyledPreviewText>
                </Flex>
              </Flex>
              <Flex column>
                <Switch size="medium" on={hasSupportUkraineFooter} onChange={toggleDeactivateFooterModal} />
              </Flex>
            </UkraineFooterWrapper>
          )}
          {!isEmbeddedInIframe && (
            <Text>
              Personalize the look of your emails with email templates. Make all your messages match your brand.{" "}
              <Button
                target="_blank"
                kind="link-light"
                href={HelpCenter.EMAIL_TEMPLATE}
                onClick={() => trackEngagement("Email templates: Learn more", EventPlace.Settings)}
              >
                Learn more
              </Button>
            </Text>
          )}
          <Heading as="h2">Create email templates</Heading>
          <Text css={isEmbeddedInIframe ? css({ maxWidth: "720px" }) : undefined}>
            Use the built-in editor to prepare HTML and plain text versions of your email template. Enrich it with a
            logo, footer, or personalized custom variables. Send a test message to the provided email address.
          </Text>
          {!isEmbeddedInIframe && (
            <>
              <Block marginTop={withVar(SpacingToken.Spacing4)}>
                <Button
                  size="compact"
                  kind="primary"
                  onClick={toggleEditorTypeModal}
                  data-testid="create-template"
                  disabled={!isAdmin || maxTemplatesLimitReached}
                >
                  Create email template
                </Button>
              </Block>
              {maxTemplatesLimitReached && (
                <Block marginTop={withVar(SpacingToken.Spacing5)}>
                  <UpsellAlert source="emailTemplates" />
                </Block>
              )}
            </>
          )}
          {!isAdmin && (
            <Block maxWidth="800px" marginTop={withVar(SpacingToken.Spacing5)}>
              <Alert>
                <Text>
                  You can&apos;t manage email templates in your current {isEmbeddedInIframe ? "" : "HelpDesk "} role.{" "}
                  {isEmbeddedInIframe ? (
                    <Button kind="link" css={css({ fontWeight: 400 })} onClick={() => navigateTo("/admin/teammates")}>
                      Contact one of your Admins
                    </Button>
                  ) : (
                    <StyledLink
                      kind="link-light"
                      to="/agents"
                      onClick={() => trackEngagement("Email templates: Contact our sales team", EventPlace.Settings)}
                    >
                      Contact any admin
                    </StyledLink>
                  )}{" "}
                  to change your role{isEmbeddedInIframe ? "." : " to admin."}
                </Text>
              </Alert>
            </Block>
          )}
          <Block marginBottom="46px" />
          <Table
            columns={4}
            marginTop="20px"
            marginBottom="40px"
            columnSizes="1fr 1fr 1fr 70px"
            headers={["Name", "Teams", "Mode", ""]}
          >
            {templates.map(template => {
              const isDefault = template.id === defaultTemplateId;
              // FIXME: Fix this the next time the file is edited.
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const assignedTeams = teams.filter((team: any) => team.templateId === template.id);
              const isCreatedUsingVisualBuilder = template.type === EmailTemplateTypeEnum.VISUAL;
              return (
                <Row key={template.id}>
                  <Column>
                    {isAdmin ? (
                      <Button
                        kind="link-light"
                        onClick={() => history.push(`${SettingsRoutes.EmailTemplates}/edit/${template.id}`)}
                        data-testid={`edit-email-template-${template.id}`}
                        css={css({ textAlign: "left" })}
                      >
                        <span css={css({ marginRight: "10px" })}>{template.name}</span>
                        {isDefault && (
                          <Tag size="small" data-test-id={`default-email-template-${template.id}`}>
                            Default
                          </Tag>
                        )}
                      </Button>
                    ) : (
                      <>
                        <span css={css({ marginRight: "10px" })}>{template.name}</span>
                        {isDefault && (
                          <Tag size="small" data-test-id={`default-email-template-${template.id}`}>
                            Default
                          </Tag>
                        )}
                      </>
                    )}
                  </Column>
                  <Column>
                    {isDefault && (
                      <Flex alignItems="center" gap={withVar(SpacingToken.Spacing1)}>
                        <Text>Unassigned teams</Text>
                        <Tooltip
                          placement="top"
                          triggerRenderer={() => <Icon source={Info} size="small" />}
                          kind="invert"
                        >
                          <Text>
                            All teams without a specific email template assigned will use this template as the default.
                          </Text>
                        </Tooltip>
                      </Flex>
                    )}
                    {!assignedTeams.length && isDefault ? null : (
                      <TeamsPreview teams={assignedTeams} previewCount={isDefault ? 0 : 1} />
                    )}
                  </Column>
                  <Column>
                    <Text>{isCreatedUsingVisualBuilder ? "Visual builder" : "HTML editor"}</Text>
                  </Column>
                  <Column>
                    <TemplateListContextMenu
                      id={template.id}
                      disabled={!isAdmin}
                      disabledDuplicate={maxTemplatesLimitReached}
                      isDefault={isDefault}
                      onDelete={handleDeleteClick}
                      isCreatedUsingVisualBuilder={isCreatedUsingVisualBuilder}
                      initialTemplate={template.createdByType === "system"}
                      data-testid={`context-menu-button-email-template-${template.id}`}
                    />
                  </Column>
                </Row>
              );
            })}
          </Table>
        </ContentWrapper>
      </PageWrapper>
      {deactivateFooterModal && (
        <ConfirmActionModal
          heading="Deactivate special footer?"
          onClose={toggleDeactivateFooterModal}
          primaryAction={{
            label: "Deactivate",
            onClick: toggleSupportUkraineFooter,
            actionType: "destructive",
          }}
          secondaryAction={{
            label: "Cancel",
            onClick: toggleDeactivateFooterModal,
          }}
        >
          If you deactivate this special footer, you won&apos;t be able to set it up again, and it&apos;ll disappear
          from your email template settings forever.
        </ConfirmActionModal>
      )}
      {deleteTemplateModal && (
        <ConfirmActionModal
          heading="Delete email template?"
          onClose={toggleDeleteTemplate}
          primaryAction={{
            label: "Delete",
            onClick: handleDeleteTemplate,
            actionType: "destructive",
            dataTestId: "delete-template",
          }}
          secondaryAction={{
            label: "Cancel",
            onClick: toggleDeleteTemplate,
          }}
        >
          If you delete this email template, the teams assigned to this template will start using the default template.
        </ConfirmActionModal>
      )}
      {editorTypeModal && (
        <EditorTypeModal
          toggleEditorTypeModal={toggleEditorTypeModal}
          onHTMLEditorClick={handleGoToHTMLEditor}
          onVisualBuilderClick={handleGoToVisualBuilder}
        />
      )}
    </SettingsViewContainer>
  );
};

const IframeHeader = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: ${withVar(SpacingToken.Spacing5)};
  padding-right: ${withVar(SpacingToken.Spacing3)};
  height: 52px;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing2)};
  border-bottom: 1px solid ${withVar(DesignToken.BorderBasicSecondary)};
`;
